/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  EnhancedObjective,
  ObjectiveCategory,
  ObjectivesState,
  ObjectiveProgress
} from '../../types/enhancedObjectives';
import { enhancedObjectivesManager } from '../../services/enhancedObjectivesManager';
import { ENHANCED_OBJECTIVES } from '../../constants/enhancedObjectives';

interface EnhancedObjectivesPanelProps {
  onClose?: () => void;
  className?: string;
}

const EnhancedObjectivesPanel: React.FC<EnhancedObjectivesPanelProps> = ({
  onClose,
  className = ''
}) => {
  const [objectivesState, setObjectivesState] = useState<ObjectivesState>(
    enhancedObjectivesManager.getState()
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedObjectives, setExpandedObjectives] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(true);
  const [viewMode, setViewMode] = useState<'tree' | 'grid' | 'kanban'>('tree');

  // Initialize objectives on mount
  useEffect(() => {
    // Load all enhanced objectives into the manager
    ENHANCED_OBJECTIVES.forEach(objective => {
      enhancedObjectivesManager.addObjective(objective);
    });

    // Set up notification callback
    enhancedObjectivesManager.setNotificationCallback((notification) => {
      console.log('Objective notification:', notification);
      // Could integrate with existing notification system
    });

    // Update state
    setObjectivesState(enhancedObjectivesManager.getState());

    // Expand first category by default
    const categories = enhancedObjectivesManager.getState().categories;
    if (categories.length > 0) {
      setExpandedCategories(new Set([categories[0].id]));
    }
  }, []);

  // Update state periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setObjectivesState(enhancedObjectivesManager.getState());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Memoized filtered objectives
  const filteredObjectives = useMemo(() => {
    const allObjectives = enhancedObjectivesManager.getObjectivesWithProgress();

    return allObjectives.filter(({ objective }) => {
      // Category filter
      if (selectedCategory && objective.categoryId !== selectedCategory) {
        return false;
      }

      // Completed filter
      if (!showCompleted && objective.status === 'completed') {
        return false;
      }

      // Hidden filter
      if (objective.isHidden) {
        return false;
      }

      return true;
    });
  }, [selectedCategory, showCompleted]);

  // Category statistics
  const categoryStats = useMemo(() => {
    return enhancedObjectivesManager.getCategoryStats();
  }, []);

  // Recommended objectives
  const recommendedObjectives = useMemo(() => {
    return enhancedObjectivesManager.getRecommendedObjectives(3);
  }, []);

  // UI Handlers
  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const toggleObjectiveExpansion = (objectiveId: string) => {
    setExpandedObjectives(prev => {
      const newSet = new Set(prev);
      if (newSet.has(objectiveId)) {
        newSet.delete(objectiveId);
      } else {
        newSet.add(objectiveId);
      }
      return newSet;
    });
  };

  const setFocusObjective = (objectiveId: string | null) => {
    enhancedObjectivesManager.setObjectiveFocus(objectiveId);
    setObjectivesState(enhancedObjectivesManager.getState());
  };

  const renderStatusBadge = (status: EnhancedObjective['status']) => {
    const statusConfig = {
      locked: { color: 'bg-gray-500', text: 'Locked', icon: '🔒' },
      available: { color: 'bg-blue-500', text: 'Available', icon: '🎯' },
      in_progress: { color: 'bg-yellow-500', text: 'In Progress', icon: '⚡' },
      completed: { color: 'bg-green-500', text: 'Completed', icon: '✅' },
      failed: { color: 'bg-red-500', text: 'Failed', icon: '❌' }
    };

    const config = statusConfig[status];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${config.color}`}>
        {config.icon} {config.text}
      </span>
    );
  };

  const renderDifficultyBadge = (difficulty: EnhancedObjective['difficulty']) => {
    const difficultyConfig = {
      trivial: { color: 'bg-green-500', text: 'Trivial' },
      easy: { color: 'bg-blue-500', text: 'Easy' },
      medium: { color: 'bg-yellow-500', text: 'Medium' },
      hard: { color: 'bg-orange-500', text: 'Hard' },
      expert: { color: 'bg-red-500', text: 'Expert' }
    };

    const config = difficultyConfig[difficulty];
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium text-white ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const renderProgressBar = (progress: number, maxValue: number) => {
    const percentage = maxValue > 0 ? (progress / maxValue) * 100 : 0;

    return (
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${
            percentage >= 100 ? 'bg-green-500' :
            percentage >= 75 ? 'bg-blue-500' :
            percentage >= 50 ? 'bg-yellow-500' :
            percentage >= 25 ? 'bg-orange-500' : 'bg-red-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    );
  };

  const renderObjectiveCard = ({ objective, progress, progressPercentage }: {
    objective: EnhancedObjective;
    progress: ObjectiveProgress | null;
    progressPercentage: number;
  }) => {
    const isExpanded = expandedObjectives.has(objective.id);
    const isFocused = objectivesState.currentFocus === objective.id;

    return (
      <div
        key={objective.id}
        className={`bg-slate-800 rounded-lg border transition-all duration-200 ${
          isFocused ? 'border-blue-400 shadow-lg shadow-blue-400/20' : 'border-slate-700'
        } ${objective.status === 'available' ? 'hover:border-blue-500 cursor-pointer' : ''}`}
        onClick={() => {
          if (objective.status === 'available') {
            setFocusObjective(objective.id);
          }
        }}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className={`font-semibold ${objective.status === 'completed' ? 'text-green-400' : 'text-white'}`}>
                  {objective.title}
                </h4>
                {isFocused && <span className="text-blue-400">🎯</span>}
              </div>
              <p className="text-sm text-slate-400">{objective.description}</p>
            </div>
            <div className="flex flex-col gap-1 items-end">
              {renderStatusBadge(objective.status)}
              {renderDifficultyBadge(objective.difficulty)}
            </div>
          </div>

          {/* Progress */}
          {objective.status !== 'locked' && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-slate-400 mb-1">
                <span>Progress</span>
                <span>{objective.progress}/{objective.maxValue} ({Math.round(progressPercentage)}%)</span>
              </div>
              {renderProgressBar(objective.progress, objective.maxValue)}
            </div>
          )}

          {/* Quick Info */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-3">
              <span className="text-slate-400">
                {objective.timeLimit && (
                  <span className={`${
                    objective.status === 'in_progress' ? 'text-yellow-400' : 'text-slate-400'
                  }`}>
                    ⏱️ {objective.timeLimit} ticks
                  </span>
                )}
              </span>
              <span className="text-slate-400">
                {objective.rewards.length > 0 && (
                  <span>🏆 {objective.rewards.length} reward{objective.rewards.length > 1 ? 's' : ''}</span>
                )}
              </span>
            </div>
            {(objective.detailedDescription || objective.hints.length > 0) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleObjectiveExpansion(objective.id);
                }}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                {isExpanded ? '▼' : '▶'} Details
              </button>
            )}
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-slate-700 space-y-3">
              {objective.detailedDescription && (
                <div>
                  <h5 className="text-xs font-semibold text-slate-300 mb-1">Overview</h5>
                  <p className="text-xs text-slate-400">{objective.detailedDescription}</p>
                </div>
              )}

              {objective.successMetrics && (
                <div>
                  <h5 className="text-xs font-semibold text-slate-300 mb-1">Success Criteria</h5>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li className="flex items-center gap-1">
                      <span className="text-green-400">✓</span>
                      {objective.successMetrics.primary}
                    </li>
                    {objective.successMetrics.secondary?.map((metric, index) => (
                      <li key={index} className="flex items-center gap-1">
                        <span className="text-blue-400">•</span>
                        {metric}
                      </li>
                    ))}
                    {objective.successMetrics.tertiary?.map((metric, index) => (
                      <li key={index} className="flex items-center gap-1">
                        <span className="text-slate-400">○</span>
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {objective.hints.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-slate-300 mb-1">Hints</h5>
                  <ul className="text-xs text-slate-400 space-y-1">
                    {objective.hints.map((hint, index) => (
                      <li key={index} className="flex items-start gap-1">
                        <span className="text-yellow-400 mt-0.5">💡</span>
                        <span>{hint}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {objective.rewards.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-slate-300 mb-1">Rewards</h5>
                  <div className="flex flex-wrap gap-1">
                    {objective.rewards.map((reward, index) => (
                      <span key={index} className="px-2 py-1 bg-green-900/30 text-green-400 rounded text-xs">
                        {reward.type === 'money' && `$${reward.value}`}
                        {reward.type === 'building_unlock' && `🏗️ ${reward.value}`}
                        {reward.type === 'product_unlock' && `💼 ${reward.value}`}
                        {reward.type === 'stat_boost' && `📈 ${reward.value}`}
                        {reward.type === 'special' && `🌟 ${reward.description}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {objective.prerequisites.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-slate-300 mb-1">Prerequisites</h5>
                  <div className="flex flex-wrap gap-1">
                    {objective.prerequisites.map((prereq, index) => (
                      <span key={index} className="px-2 py-1 bg-red-900/30 text-red-400 rounded text-xs">
                        {prereq.description}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTreeView = () => (
    <div className="space-y-4">
      {categoryStats.map(({ category, totalObjectives, completedObjectives, inProgressObjectives, completionPercentage }) => {
        const isExpanded = expandedCategories.has(category.id);
        const categoryObjectives = filteredObjectives.filter(({ objective }) =>
          objective.categoryId === category.id
        );

        return (
          <div key={category.id} className="bg-slate-900 rounded-lg border border-slate-700">
            {/* Category Header */}
            <div
              className={`p-4 cursor-pointer transition-colors ${
                isExpanded ? 'border-b border-slate-700' : ''
              }`}
              onClick={() => toggleCategoryExpansion(category.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button className="text-slate-400 hover:text-white transition-colors">
                    {isExpanded ? '▼' : '▶'}
                  </button>
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{category.name}</h3>
                    <p className="text-sm text-slate-400">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm text-slate-400">
                      {completedObjectives}/{totalObjectives} completed
                    </div>
                    <div className="text-xs text-slate-500">
                      {inProgressObjectives} in progress
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">{Math.round(completionPercentage)}%</div>
                    {renderProgressBar(completedObjectives, totalObjectives)}
                  </div>
                </div>
              </div>
            </div>

            {/* Category Objectives */}
            {isExpanded && (
              <div className="p-4 space-y-3">
                {categoryObjectives.length === 0 ? (
                  <div className="text-center text-slate-400 py-8">
                    No objectives in this category yet
                  </div>
                ) : (
                  categoryObjectives.map((objectiveData) => (
                    <div key={objectiveData.objective.id} className="pl-4">
                      {renderObjectiveCard(objectiveData)}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredObjectives.map((objectiveData) => (
        renderObjectiveCard(objectiveData)
      ))}
    </div>
  );

  const renderKanbanView = () => {
    const columns = [
      { id: 'available', title: 'Available', color: 'border-blue-500' },
      { id: 'in_progress', title: 'In Progress', color: 'border-yellow-500' },
      { id: 'completed', title: 'Completed', color: 'border-green-500' }
    ];

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {columns.map((column) => {
          const columnObjectives = filteredObjectives.filter(({ objective }) =>
            objective.status === column.id
          );

          return (
            <div key={column.id} className={`bg-slate-900 rounded-lg border ${column.color} border-opacity-50`}>
              <div className={`p-4 border-b ${column.color} border-opacity-50`}>
                <h3 className="text-lg font-semibold text-white">{column.title}</h3>
                <div className="text-sm text-slate-400">{columnObjectives.length} objectives</div>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {columnObjectives.map((objectiveData) => (
                  <div key={objectiveData.objective.id}>
                    {renderObjectiveCard(objectiveData)}
                  </div>
                ))}
                {columnObjectives.length === 0 && (
                  <div className="text-center text-slate-400 py-8">
                    No objectives in this column
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`bg-slate-900 rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col ${className}`}>
      {/* Header */}
      <div className="border-b border-slate-700 p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="text-3xl">🎯</span>
              Enhanced Objectives
            </h2>
            <p className="text-slate-400 mt-1">Categorized objectives with advanced tracking and rewards</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-2xl leading-none transition-colors"
            >
              ×
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'tree' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              🌳 Tree
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              ⊞ Grid
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'kanban' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              📋 Kanban
            </button>
          </div>

          <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                selectedCategory === null ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              All
            </button>
            {objectivesState.categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  selectedCategory === category.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {category.icon} {category.name}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-900"
            />
            <span className="text-sm">Show Completed</span>
          </label>
        </div>
      </div>

      {/* Recommended Objectives */}
      {recommendedObjectives.length > 0 && viewMode === 'tree' && (
        <div className="border-b border-slate-700 p-4 bg-slate-800/50">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            ⭐ Recommended Objectives
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recommendedObjectives.map((objective) => (
              <div key={objective.id} className="bg-slate-700 rounded-lg p-3 border border-slate-600">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-white text-sm">{objective.title}</h4>
                  {renderDifficultyBadge(objective.difficulty)}
                </div>
                <p className="text-xs text-slate-400 mb-2">{objective.description}</p>
                <button
                  onClick={() => setFocusObjective(objective.id)}
                  className="w-full px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded transition-colors"
                >
                  Focus on This
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {viewMode === 'tree' && renderTreeView()}
        {viewMode === 'grid' && renderGridView()}
        {viewMode === 'kanban' && renderKanbanView()}
      </div>
    </div>
  );
};

export default EnhancedObjectivesPanel;